import { z } from 'zod';
import { TechniqueId } from '../rules.js';

/**
 * TARIFAS COMO DATOS (P4). Ni un `if` de negocio en el codigo: cambiar un precio
 * es editar una fila vigente, y el cambio queda versionado y auditable.
 *
 * Todo el dinero viaja en CENTIMOS ENTEROS. Sumar euros en coma flotante da
 * 0.1 + 0.2 = 0.30000000000000004, y eso acaba en una factura.
 */

export const SurchargeCondition = z.object({
  /** Se aplica si el area marcada supera estos mm2. */
  areaMm2Over: z.number().positive().optional(),
  /** Se aplica si el numero de tintas supera esta cifra. */
  inksOver: z.number().int().nonnegative().optional(),
  /** Se aplica si el diseno usa blanco tecnico (subbase). */
  requiresTechnicalWhite: z.boolean().optional(),
  /** Se aplica si la prenda es de color oscuro (necesita subbase). */
  darkGarment: z.boolean().optional(),
});
export type SurchargeCondition = z.infer<typeof SurchargeCondition>;

export const Surcharge = z.object({
  /** Codigo estable; la UI lo traduce. */
  code: z.string().min(1),
  kind: z.enum(['fixed', 'per_unit', 'percent']),
  /** Centimos para `fixed`/`per_unit`; puntos porcentuales (x100) para `percent`. */
  value: z.number().int(),
  appliesWhen: SurchargeCondition.default({}),
});
export type Surcharge = z.infer<typeof Surcharge>;

export const PriceRuleVersion = z.object({
  id: z.string().min(1),
  version: z.string().min(1),
  validFrom: z.string().datetime(),
  validUntil: z.string().datetime().nullable(),

  techniqueId: TechniqueId,
  /** `null` = aplica a cualquier zona. */
  zoneId: z.string().nullable(),

  /** Tramo de cantidad TOTAL del pedido (tiradas mixtas incluidas). */
  qtyFrom: z.number().int().positive(),
  qtyTo: z.number().int().positive().nullable(),

  /** Preparacion del pedido (fotolitos, digitalizacion, calibrado de maquina). */
  setupCents: z.number().int().nonnegative(),
  /** Preparacion adicional POR TINTA (cada tinta es una pantalla). */
  perInkSetupCents: z.number().int().nonnegative(),
  /** Marcaje por unidad. */
  unitMarkingCents: z.number().int().nonnegative(),
  /** Recargo por tinta y unidad. */
  perInkUnitCents: z.number().int().nonnegative(),
  /** Recargo por pasada y unidad. */
  perPassUnitCents: z.number().int().nonnegative(),

  surcharges: z.array(Surcharge).default([]),
});
export type PriceRuleVersion = z.infer<typeof PriceRuleVersion>;

export const PriceBook = z.object({
  id: z.string().min(1),
  version: z.string().min(1),
  /** IVA en puntos porcentuales x100: 2100 = 21 %. */
  vatBasisPoints: z.number().int().nonnegative(),
  /** Minimo de pedido en centimos. */
  minOrderCents: z.number().int().nonnegative(),
  /** Horas de validez del presupuesto. Un precio caducado NO se honra en silencio. */
  validityHours: z.number().int().positive(),
  rules: z.array(PriceRuleVersion),
});
export type PriceBook = z.infer<typeof PriceBook>;

export class NoApplicableRuleError extends Error {
  constructor(readonly techniqueId: string, readonly zoneId: string, readonly qty: number) {
    super(`sin tarifa vigente para ${techniqueId} en la zona ${zoneId} con ${qty} unidades`);
    this.name = 'NoApplicableRuleError';
  }
}

/**
 * Selecciona la tarifa aplicable. El tramo se decide por la cantidad TOTAL del
 * pedido, no por linea: en B2B la tirada mixta (S, M, L del mismo diseno) es el
 * caso normal, y cobrar cada talla como si fuera un pedido pequeno es un error
 * de negocio, no de redondeo.
 *
 * Ante varias reglas aplicables gana la de tramo mas estrecho; a igualdad, la de
 * `validFrom` mas reciente. El desempate es DETERMINISTA a proposito.
 */
export function selectPriceRule(
  book: PriceBook,
  techniqueId: string,
  zoneId: string,
  totalQty: number,
  nowIso: string,
): PriceRuleVersion {
  const now = Date.parse(nowIso);
  const candidates = book.rules.filter((rule) => {
    if (rule.techniqueId !== techniqueId) return false;
    if (rule.zoneId !== null && rule.zoneId !== zoneId) return false;
    if (totalQty < rule.qtyFrom) return false;
    if (rule.qtyTo !== null && totalQty > rule.qtyTo) return false;
    if (now < Date.parse(rule.validFrom)) return false;
    if (rule.validUntil !== null && now > Date.parse(rule.validUntil)) return false;
    return true;
  });

  if (candidates.length === 0) throw new NoApplicableRuleError(techniqueId, zoneId, totalQty);

  const width = (r: PriceRuleVersion) => (r.qtyTo === null ? Number.MAX_SAFE_INTEGER : r.qtyTo - r.qtyFrom);
  candidates.sort((a, b) => {
    // Zona concreta gana a "cualquier zona".
    const za = a.zoneId === null ? 1 : 0;
    const zb = b.zoneId === null ? 1 : 0;
    if (za !== zb) return za - zb;
    const w = width(a) - width(b);
    if (w !== 0) return w;
    const f = Date.parse(b.validFrom) - Date.parse(a.validFrom);
    if (f !== 0) return f;
    return a.id < b.id ? -1 : 1;
  });

  return candidates[0]!;
}
